import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { PlusIcon, PencilIcon, ArchiveBoxIcon, ChartBarIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { ANNOUNCEMENTS } from '../../graphql/announcement-queries'
import { ARCHIVE_ANNOUNCEMENT } from '../../graphql/announcement-mutations'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import LoadingSpinner from '../../components/LoadingSpinner'
import AnnouncementTypeBadge from '../../components/announcements/AnnouncementTypeBadge'
import { formatRelativeTime } from '../../components/announcements/announcementFormat'
import type { Announcement, AnnouncementStatus } from '../../types/announcement'

type StatusFilter = 'all' | 'published' | 'archived'

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
]

const STATUS_STYLES: Record<AnnouncementStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
}

const AnnouncementsAdminPage = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canWrite = hasPermission(PERMISSIONS.MESSAGE_WRITE)
  const canSeeReceipts = hasPermission(PERMISSIONS.MESSAGE_READ_RECEIPTS)

  const [status, setStatus] = useState<StatusFilter>('all')

  const { data, loading, refetch } = useQuery(ANNOUNCEMENTS, {
    variables: { status: status === 'all' ? undefined : status, limit: 50, offset: 0 },
    fetchPolicy: 'cache-and-network',
  })
  const announcements: Announcement[] = data?.announcements?.announcements ?? []

  const [archive] = useMutation(ARCHIVE_ANNOUNCEMENT)

  const handleArchive = async (a: Announcement) => {
    if (!window.confirm(`Archive "${a.title}"? It leaves the active feed and outstanding ack obligations are cleared. Receipts are retained.`)) return
    try {
      await archive({ variables: { id: a.id } })
      toast.success('Archived')
      void refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Announcements</h1>
          <p className="mt-1 text-sm text-gray-500">Create, edit, archive, and review who's read each announcement.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Link to="/admin/announcements/types" className="btn-secondary">Manage types</Link>
            <Link to="/admin/announcements/new" className="btn-primary flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              New Announcement
            </Link>
          </div>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-1 border-b border-gray-200">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              status === s.key ? 'border-primary-500 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && announcements.length === 0 ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : announcements.length === 0 ? (
        <div className="card py-12 text-center text-sm text-gray-400">No announcements in this view.</div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {announcements.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <AnnouncementTypeBadge type={a.type} />
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[a.status]}`}>{a.status}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-gray-900 truncate">{a.title}</p>
                <p className="text-xs text-gray-400">
                  {a.author?.name ? `${a.author.name} · ` : ''}
                  {a.status === 'published' && a.published_at ? formatRelativeTime(a.published_at) : formatRelativeTime(a.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canSeeReceipts && a.status !== 'draft' && (
                  <button
                    onClick={() => navigate(`/admin/announcements/${a.id}/receipts`)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <ChartBarIcon className="h-4 w-4" />
                    Receipts
                  </button>
                )}
                {canWrite && a.status !== 'archived' && (
                  <button
                    onClick={() => navigate(`/admin/announcements/${a.id}/edit`)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                )}
                {canWrite && a.status === 'published' && (
                  <button
                    onClick={() => handleArchive(a)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    <ArchiveBoxIcon className="h-4 w-4" />
                    Archive
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AnnouncementsAdminPage
